import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["autoload", "deferAutoloadUntilOpen", "filterOptions", "onOpenChange", "ignoreCase", "ignoreAccents", "referenceToOption", "options", "resolver", "searchCount", "loadReferences", "value"];
import { referenceToOption as defaultReferenceToOption } from '../utils/referenceToOption';
import { forwardRef, useMemo, useEffect, useRef, useState, useCallback } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import UISelect from 'UIComponents/input/UISelect';
import { asHiddenProp } from 'UIComponents/utils/asHiddenProp';
import debounce from 'react-utils/debounce';
import allSettled from 'hs-promise-utils/allSettled';
import { SkeletonText } from './SkeletonText';
import { jsx as _jsx } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
function getUniqueOptions(options) {
  const hasValue = new Set();
  return options.filter(option => {
    if (!option) {
      return false;
    }
    if (hasValue.has(option.value)) {
      return false;
    }
    hasValue.add(option.value);
    return true;
  });
}
const idIsValid = id => {
  if (id === undefined || id === null || id === '') {
    return false;
  }
  return true;
};
const getMissingIds = memoizeOne((value, options) => {
  const valueArray = Array.isArray(value) ? value : [value];
  return valueArray.filter(idIsValid).filter(valueElement => !options.find(option => option && option.value === valueElement));
});

// Grab the values that could be successfully resolved and throw out any failed requests
const getFulfilledPromiseValues = promises => promises.reduce((acc, promise) => {
  if (promise.status === 'fulfilled') {
    acc.push(promise.value);
  }
  return acc;
}, []);
const defaultLoadReferences = (resolver, searchQuery) => {
  return resolver.api.search(searchQuery);
};
const buildLoadingOption = id => ({
  value: id,
  text: id,
  buttonText: /*#__PURE__*/_jsx(SkeletonText, {
    use: "form-on-dark"
  })
});
const buildErrorOption = id => ({
  value: id,
  text: id
});
const loadOptions = (options, resolver, searchCount, searchOptionsCache, query, callback, pagination = {
  offset: 0
}, referenceToOption, loadReferences) => {
  const {
    offset
  } = pagination;
  const searchQuery = {
    count: searchCount,
    offset,
    query
  };

  // Reset the search option cache if the pagination reset
  if (offset === 0) {
    searchOptionsCache.current = [];
  }
  loadReferences(resolver, searchQuery).then(({
    hasMore,
    offset: newOffset,
    references
  }) => {
    searchOptionsCache.current = searchOptionsCache.current.concat(references.map(referenceToOption));
    const allOptions = getUniqueOptions(options.concat(searchOptionsCache.current));
    callback(null, {
      options: allOptions,
      pagination: {
        hasMore,
        offset: newOffset
      }
    });
  }).catch(() => {});
};
// This is defined outside the component because if we set options to an empty
// array inside the component it constantly get re-created
const emptyArray = [];
const ReferenceLiteSearchSelect = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
      autoload = true,
      deferAutoloadUntilOpen = false,
      filterOptions = false,
      onOpenChange,
      ignoreCase = asHiddenProp(false),
      ignoreAccents = asHiddenProp(false),
      referenceToOption = defaultReferenceToOption,
      options = emptyArray,
      resolver,
      searchCount = 25,
      loadReferences = defaultLoadReferences,
      value
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const searchOptionsCache = useRef([]);
  const [valueOptions, setValueOptions] = useState([]);
  const resolverKey = useRef({
    resolver,
    key: 0
  });

  // When `resolver` changes, bump `key` to force a re-mount of the `UISelect`
  // in order to clear its option and pagination caches. Otherwise, switching
  // to a new resolver which returns different references will continue showing
  // stale cached options from the previous resolver.
  //
  // NOTE: if resolvers exposed a cache key, we could use that instead. then
  // changing resolvers would only need to force a re-mount if the new resolver
  // had a different cache key (i.e. a different reference type or parameters)
  if (resolver !== resolverKey.current.resolver) {
    searchOptionsCache.current = [];
    resolverKey.current.resolver = resolver;
    resolverKey.current.key = resolverKey.current.key + 1;
  }
  useEffect(() => {
    const allOptions = options.concat(searchOptionsCache.current);
    const missingIds = getMissingIds(value, allOptions);
    if (missingIds.length === 0) {
      setValueOptions([]);
      return;
    }

    // Due to UISelect's options cache, once an option has been provided for a value,
    // it will be reused until a new option is provided for the value.
    //
    // Because loading options are created for all ids to show the loading
    // state, after a success, partial success, or failure, new options
    // must be built for ALL values, including any that failed.
    const fetchPromises = missingIds.map(missingId => {
      return resolver.api.byId(missingId);
    });
    setValueOptions(missingIds.map(buildLoadingOption));
    allSettled(fetchPromises).then(settledPromises => {
      const references = getFulfilledPromiseValues(settledPromises);
      const validIds = new Set(references.map(reference => reference.id));
      const errorIds = missingIds.filter(id => !validIds.has(id));
      setValueOptions([...references.map(referenceToOption), ...errorIds.map(buildErrorOption)]);
    }).catch(() => {
      setValueOptions(missingIds.map(buildErrorOption));
    });
  }, [options, value, resolver, referenceToOption]);
  const debouncedLoadOptions = useMemo(() => debounce((query, callback, pagination) => loadOptions(options, resolver, searchCount, searchOptionsCache, query, callback, pagination, referenceToOption, loadReferences), 500), [options, resolver, searchCount, referenceToOption, loadReferences]);
  const {
    defaultOpen,
    open
  } = rest;
  const openedRef = useRef(Boolean(defaultOpen || open || false));
  const handleLoadOptions = useCallback((query, callback, pagination) => {
    // Defer loading options until the select opens. This avoids hitting
    // the API for every select when the page first loads, but still shows
    // the first page of options when the select opens (which `autoload={false}`
    // does not).
    //
    // UISelect does not support deferred autoloading of async options
    // (see https://git.hubteam.com/HubSpot/UIComponents/issues/12791),
    // so we use a workaround:
    //
    // 1. `<UISelect anchorType="button" autoload={true} />` renders an offscreen
    //    `Select.Async` to load options immediately on mount. See
    //     [`UISelectWithButtonAnchor.componentDidMount`](https://git.hubteam.com/HubSpot/UIComponents/blob/dea7b79311b8e6ba1a2c54b80f77b4e825707be9/UIComponents/static-3.3455/js/input/internal/UISelectWithButtonAnchor.js#L134-L152)
    // 2. Ignore calls to `loadOptions` that arrive before the select is open
    // 3. Opening the select calls `onOpenChange`; set a flag so we know the select has been opened
    // 4. After the select opens, UISelect renders an onscreen `Select.Async`,
    //    which calls `loadOptions` again. This time, load the options.
    if (deferAutoloadUntilOpen && !openedRef.current) {
      return;
    }

    // We don't debounce non-search queries in order to work around a race
    // condition: https://git.hubteam.com/HubSpot/UIComponents/issues/10718
    // that happens with async loadOptions and automatically opened
    // UISelects.
    if (!query) {
      loadOptions(options, resolver, searchCount, searchOptionsCache, query, callback, pagination, referenceToOption, loadReferences);
      return;
    }
    debouncedLoadOptions(query, callback, pagination);
  }, [debouncedLoadOptions, options, resolver, searchCount, referenceToOption, loadReferences, deferAutoloadUntilOpen]);
  const handleOpenChange = useCallback(event => {
    if (event.target.value) {
      openedRef.current = true;
    }
    if (onOpenChange) {
      onOpenChange(event);
    }
  }, [onOpenChange]);

  // Options passed to the wrapped `<UISelect options={...} />`
  //
  // The UISelect `options` prop doesn't control what options are visible in
  // the dropdown when async mode is on (`loadOptions`), it just allows us to
  // resolve options that are currently used in `value`. This makes it so we
  // can resolve value options into human-readable labels before opening the
  // dropdown, it does not make the option appear in the dropdown (that is
  // handled by including `options` in the `loadOptions` callback above).
  // It is a union of two lists:
  //
  // `options`      - `options` prop passed from the parent. options that the
  //                  parent is aware of, separate from any options fetched from RRL
  // `valueOptions` - options fetched dynamically from RRL for each `value`
  //                  that is not present in `options`, so labels can be shown
  //                  before the dropdown is opened, and for options not in the
  //                  first page of search results
  const uiselectOptions = useMemo(() => getUniqueOptions([...options, ...valueOptions]), [options, valueOptions]);
  return /*#__PURE__*/_createElement(UISelect, Object.assign({}, rest, {
    autoload: autoload,
    anchorType: "button",
    filterOptions: filterOptions,
    ignoreCase: ignoreCase,
    ignoreAccents: ignoreAccents,
    key: resolverKey.current.key,
    loadOptions: handleLoadOptions,
    options: uiselectOptions,
    ref: ref,
    value: value,
    onOpenChange: handleOpenChange
  }));
});
ReferenceLiteSearchSelect.displayName = 'ReferenceLiteSearchSelect';
export default ReferenceLiteSearchSelect;